// src/graphql/queries/jobQueries.js

// Get a single job by ID (for deep linking)
export const getJob = () => `query GetJob($id: String!) {
    job(id: $id) {
        id
        type
        status
        tenderId
        parameters
        result
        error
        startedAt
        completedAt
        cronSchedule
        enabled
        isScheduled
        lastScheduledRun
        nextScheduledRun
        createdAt
        updatedAt
        progressPercent
        progressMessage
        retryCount
        maxRetries
        cancelRequested
        cancelledAt
        timeoutSeconds
        parentJobId
        heartbeatAt
        workerId
        # V39: reconciliation markers ("yaml" = owned by workflows/_schedule.yaml)
        managedBy
        managedName
    }
}`;

// List query — intentionally omits `result` (can be MBs per row for completed jobs).
// Use `getJob(id)` to fetch the full job (including `result`) when opening a detail panel.
export const getJobs = () => `query GetJobs($status: JobStatus, $type: JobType, $limit: Int, $offset: Int, $isScheduled: Boolean) {
    adminJobs(status: $status, type: $type, limit: $limit, offset: $offset, isScheduled: $isScheduled) {
        id
        type
        status
        tenderId
        parameters
        error
        startedAt
        completedAt
        cronSchedule
        enabled
        isScheduled
        lastScheduledRun
        nextScheduledRun
        createdAt
        updatedAt
        # V26: Enhanced tracking fields
        progressPercent
        progressMessage
        retryCount
        maxRetries
        cancelRequested
        cancelledAt
        timeoutSeconds
        parentJobId
        # V32: Heartbeat for worker liveness
        heartbeatAt
        workerId
        # V39: reconciliation markers ("yaml" = owned by workflows/_schedule.yaml)
        managedBy
        managedName
    }
}`;

// V26: Get job timeline with event history
export const getJobTimeline = () => `query GetJobTimeline($id: String!) {
    jobTimeline(id: $id) {
        job {
            id
            type
            status
            tenderId
            parameters
            result
            error
            startedAt
            completedAt
            progressPercent
            progressMessage
            retryCount
            maxRetries
            timeoutSeconds
            cancelRequested
            cancelledAt
            parentJobId
            # V32: Heartbeat for worker liveness
            heartbeatAt
            workerId
        }
        events {
            id
            jobId
            eventType
            timestamp
            progressPercent
            progressMessage
            details
            createdAt
        }
        totalDuration
        currentProgress
        lastEventType
    }
}`;

// V26: Get job events only (lighter query)
export const getJobEvents = () => `query GetJobEvents($jobId: String!) {
    jobEvents(jobId: $jobId) {
        id
        jobId
        eventType
        timestamp
        progressPercent
        progressMessage
        details
        createdAt
    }
}`;

export const getAssessmentCycleStatus = () => `query GetAssessmentCycleStatus {
    assessmentCycleStatus {
        status
        cycleIntervalHours
        lastCycleStartedAt
        lastCycleCompletedAt
        nextCycleAt
        currentCycleProgress
        tendersInCurrentCycle
        tendersProcessedInCurrentCycle
        currentlyProcessingTender {
            id
            tenderId
            tenderTitle
            fingerprintId
            status
            priority
            requestedAt
            startedAt
            completedAt
            score
            error
        }
        queueLength
    }
}`;

export const getFingerprintAssessmentStatus = () => `query GetFingerprintAssessmentStatus($fingerprintId: String) {
    fingerprintAssessmentStatus(fingerprintId: $fingerprintId) {
        fingerprintId
        lastAssessmentCycleAt
        tendersAssessed
        averageScore
        highScoreCount
        nextScheduledAt
    }
}`;

export const getRecentlyProcessedAssessments = () => `query GetRecentlyProcessedAssessments($fingerprintId: String, $limit: Int) {
    recentlyProcessedAssessments(fingerprintId: $fingerprintId, limit: $limit) {
        id
        tenderId
        tenderTitle
        fingerprintId
        status
        priority
        requestedAt
        startedAt
        completedAt
        score
        error
    }
}`;

// V32: Get stale jobs (running but no heartbeat within threshold)
export const getStaleJobs = () => `query GetStaleJobs($thresholdMinutes: Int!) {
    adminGetStaleJobs(thresholdMinutes: $thresholdMinutes) {
        success
        message
        jobs {
            id
            type
            status
            tenderId
            startedAt
            heartbeatAt
            workerId
            progressPercent
            progressMessage
            error
        }
        count
        error
    }
}`;

// Job Schema Queries - Dynamic job type and parameter definitions from backend

/**
 * Get all job type schemas with their parameter definitions
 * Use this to dynamically build job creation forms and validate parameters
 */
export const getJobTypeSchemas = () => `query GetJobTypeSchemas {
    jobTypeSchemas {
        jobType
        description
        params {
            name
            jsonName
            type
            required
            default
            description
        }
    }
}`;

/**
 * Get schema for a specific job type
 * @param {string} jobType - The job type to get schema for (e.g., "assess_tender", "crawl")
 */
export const getJobTypeSchema = () => `query GetJobTypeSchema($jobType: String!) {
    jobTypeSchema(jobType: $jobType) {
        jobType
        description
        params {
            name
            jsonName
            type
            required
            default
            description
        }
    }
}`;
